import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

type RowBase = {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  detail?: ReactNode;
  trailing?: ReactNode;
  destructive?: boolean;
  children?: never;
};

type RowAsLink = RowBase & {
  to: LinkProps["to"];
  params?: LinkProps["params"];
  search?: LinkProps["search"];
  onClick?: never;
  href?: never;
};
type RowAsButton = RowBase & {
  onClick: () => void;
  to?: never;
  href?: never;
};
type RowAsHref = RowBase & {
  href: string;
  to?: never;
  onClick?: never;
};
type RowAsStatic = RowBase & {
  to?: never;
  onClick?: never;
  href?: never;
};

export type RowProps = RowAsLink | RowAsButton | RowAsHref | RowAsStatic;

const PADDING = "px-4 py-3";
const HOVER = "transition-colors active:bg-[var(--fill-quaternary)]";

function Inner({
  leading,
  title,
  subtitle,
  detail,
  trailing,
  destructive,
  showChevron,
}: RowBase & { showChevron: boolean }) {
  return (
    <div className={`flex items-center gap-3 ${PADDING}`}>
      {leading && <span className="shrink-0 grid place-items-center">{leading}</span>}
      <div className="flex-1 min-w-0">
        <div
          className="text-body truncate"
          style={{ color: destructive ? "var(--system-red)" : "var(--label-primary)" }}
        >
          {title}
        </div>
        {subtitle && (
          <div className="text-footnote truncate mt-0.5" style={{ color: "var(--label-secondary)" }}>
            {subtitle}
          </div>
        )}
      </div>
      {detail && (
        <span className="text-body shrink-0 truncate max-w-[55%]" style={{ color: "var(--label-secondary)" }}>
          {detail}
        </span>
      )}
      {trailing && <span className="shrink-0">{trailing}</span>}
      {showChevron && (
        <ChevronRight
          size={16}
          aria-hidden
          className="shrink-0"
          style={{ color: "var(--label-tertiary)" }}
        />
      )}
    </div>
  );
}

export function Row(props: RowProps) {
  const showChevron = "to" in props && !!props.to;
  const inner = <Inner {...props} showChevron={showChevron} />;

  if ("to" in props && props.to) {
    return (
      <Link
        to={props.to}
        params={props.params as never}
        search={props.search as never}
        className={`block w-full text-left ${HOVER}`}
      >
        {inner}
      </Link>
    );
  }
  if ("onClick" in props && props.onClick) {
    return (
      <button type="button" onClick={props.onClick} className={`block w-full text-left ${HOVER}`}>
        {inner}
      </button>
    );
  }
  if ("href" in props && props.href) {
    return (
      <a href={props.href} className={`block w-full text-left ${HOVER}`}>
        {inner}
      </a>
    );
  }
  return <div className="block w-full text-left">{inner}</div>;
}

export function RowSeparator({ inset = 56 }: { inset?: number }) {
  return (
    <div
      role="separator"
      style={{
        marginLeft: inset,
        height: 0.5,
        background: "var(--separator)",
      }}
    />
  );
}
